import { BaseItem, PortfolioData, PrivacySettings } from "./sample.data";

/**
 * Utility functions for portfolio data management
 */

// Filter items based on privacy settings
export function filterVisibleItems<T extends BaseItem>(items: T[]): T[] {
  return items.filter((item) => item.privacy?.isVisible ?? true);
}

// Check if a section should be visible and has data
export function isSectionVisible(privacy: PrivacySettings): boolean {
  return privacy?.isVisible ?? true;
}

// Check if a section should be visible and has data
export function isSectionVisibleWithData(
  privacy: PrivacySettings,
  items?: any[],
  content?: string,
  data?: any
): boolean {
  const isVisible = privacy?.isVisible ?? true;
  if (!isVisible) return false;

  // Check if section has data
  if (items !== undefined) {
    return (
      items.length > 0 &&
      items.some((item) => item.privacy?.isVisible !== false)
    );
  }

  if (content !== undefined) {
    return content.trim().length > 0;
  }

  if (data !== undefined) {
    return Object.keys(data).length > 0;
  }

  return false; // Default to hidden if no data is provided or data is empty
}

// Sort items by order or creation date
export function sortItems<T extends BaseItem>(items: T[]): T[] {
  return items.sort((a, b) => {
    // Sort by custom order first, then by creation date
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;

    // Fall back to creation date if available
    if (a.createdAt && b.createdAt) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }

    return 0;
  });
}

// Get filtered and sorted items for a section
export function getVisibleSortedItems<T extends BaseItem>(items: T[]): T[] {
  return sortItems(filterVisibleItems(items));
}

// Get featured items
export function getFeaturedItems<T extends BaseItem & { featured?: boolean }>(
  items: T[]
): T[] {
  return filterVisibleItems(items).filter((item) => item.featured ?? false);
}

// Generate portfolio summary stats
export function generatePortfolioStats(portfolio: PortfolioData) {
  const visibleSections = Object.entries(portfolio.sections).filter(
    ([, section]) => {
      if ("items" in section) {
        return isSectionVisible(section.privacy) && section.items.length > 0;
      }
      return isSectionVisible(section.privacy);
    }
  ).length;

  const totalItems = Object.values(portfolio.sections).reduce(
    (total, section) => {
      if ("items" in section) {
        return (
          total +
          (section.items as BaseItem[]).filter(
            (item) => item.privacy?.isVisible ?? true
          ).length
        );
      }
      return total;
    },
    0
  );

  return {
    visibleSections,
    totalItems,
    lastUpdated: portfolio.metadata.lastUpdated,
    version: portfolio.metadata.version,
  };
}

// Validate portfolio data structure
export function validatePortfolioData(portfolio: PortfolioData): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check required fields
  if (!portfolio.personalInfo.name) {
    errors.push("Personal info name is required");
  }
  if (!portfolio.personalInfo.email) {
    errors.push("Personal info email is required");
  }
  if (!portfolio.contactInfo.email) {
    errors.push("Contact info email is required");
  }

  // Check for duplicate IDs within sections
  const allIds = new Set<string>();
  Object.values(portfolio.sections).forEach((section) => {
    if ("items" in section) {
      section.items.forEach((item) => {
        if (allIds.has(item.id)) {
          errors.push(`Duplicate ID found: ${item.id}`);
        }
        allIds.add(item.id);
      });
    } else if ("data" in section) {
      if (allIds.has(section.data.id)) {
        errors.push(`Duplicate ID found: ${section.data.id}`);
      }
      allIds.add(section.data.id);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Helper to get portfolio schema version
export function getSchemaVersion(): string {
  return "2.0.0";
}

// Migration helper for future schema changes
export function migratePortfolioData(
  data: any,
  fromVersion: string,
  toVersion: string
): PortfolioData {
  // This is a placeholder for future migration logic
  // When we need to change the schema, we can add migration functions here

  if (fromVersion === "1.0.0" && toVersion === "2.0.0") {
    // Example migration logic would go here
    console.log("Migrating portfolio data from v1.0.0 to v2.0.0");
  }

  return data as PortfolioData;
}

// SEO helpers
export function generateMetaTags(portfolio: PortfolioData) {
  const seo = portfolio.metadata.seoSettings || {};

  return {
    title:
      seo.title ||
      `${portfolio.personalInfo.name} - ${portfolio.personalInfo.headline}`,
    description:
      seo.description || portfolio.sections.about.content.substring(0, 160),
    keywords: seo.keywords?.join(", ") || "",
    ogImage: seo.ogImage || portfolio.personalInfo.avatar,
    ogUrl: portfolio.personalInfo.website || "",
  };
}

// Privacy level helpers
export function filterByPrivacyLevel<T extends BaseItem>(
  items: T[],
  maxLevel: "public" | "professional" | "private" = "public"
): T[] {
  const levelHierarchy = { public: 0, professional: 1, private: 2 };
  const maxLevelValue = levelHierarchy[maxLevel];

  return items.filter((item) => {
    if (!(item.privacy?.isVisible ?? true)) return false;

    const itemLevel = item.privacy?.visibilityLevel || "public";
    const itemLevelValue = levelHierarchy[itemLevel];

    return itemLevelValue <= maxLevelValue;
  });
}
